import type { ReactNode } from 'react';
import Button from './Button';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useScrollLock } from '@/hooks/useScrollLock';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  /** タイトル左に置くアイコン要素（任意） */
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** ヘッダー右側（閉じるボタンの左）に置くアクション要素（任意） */
  headerActions?: ReactNode;
  /** パネル幅（Tailwind の max-w-* クラス） */
  maxWidth?: string;
  /** パネルの高さ制限クラス */
  maxHeightClassName?: string;
  /** 本文ラッパーのクラス（既定: p-6 overflow-y-auto flex-1） */
  bodyClassName?: string;
  /** フッターのクラス（既定: 右寄せボタン用） */
  footerClassName?: string;
  /** 背景クリックで閉じる（既定 false） */
  closeOnBackdrop?: boolean;
  /** 背景の z-index クラス（既定 z-50） */
  zIndexClassName?: string;
}

const DEFAULT_BODY = 'p-4 sm:p-6 overflow-y-auto overscroll-contain flex-1';
const DEFAULT_FOOTER =
  'px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center justify-end gap-2 sm:gap-3 shrink-0';

/**
 * アプリ共通のモーダル。
 * ヘッダー（タイトル＋閉じる）／本文／フッターの構造を統一する。
 * 幅・高さ・本文／フッターのクラスを props で上書きできるので、
 * 各画面の自前モーダルはこの部品に寄せること。
 */
export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  headerActions,
  maxWidth = 'max-w-2xl',
  maxHeightClassName = 'max-h-[90vh]',
  bodyClassName = DEFAULT_BODY,
  footerClassName = DEFAULT_FOOTER,
  closeOnBackdrop = false,
  zIndexClassName = 'z-50',
}: ModalProps) {
  // Escape キーでも閉じられるようにする（開いている間だけ登録）。
  useEscapeKey(open, onClose);
  // 開いている間は背景（ページ本体）のスクロールを固定する。
  useScrollLock(open);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center ${zIndexClassName} p-4`}
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        className={`bg-white rounded-xl shadow-xl ${maxWidth} w-full ${maxHeightClassName} flex flex-col overflow-hidden`}
        onClick={closeOnBackdrop ? (e) => e.stopPropagation() : undefined}
      >
        {(title || subtitle || headerActions) && (
          <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              {icon}
              <div className="min-w-0">
                {title && <h3 className="text-lg font-bold text-gray-900">{title}</h3>}
                {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {headerActions}
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                icon="ri-close-line"
                onClick={onClose}
                aria-label="閉じる"
                className="shrink-0"
              />
            </div>
          </div>
        )}

        <div className={bodyClassName}>{children}</div>

        {footer && <div className={footerClassName}>{footer}</div>}
      </div>
    </div>
  );
}